"use client"

import { TransactionDirection } from "@/features/finance/model/finance-types"
import { cn } from "@ibada/ui"

export function isIncomeLikeTransactionType(type: unknown): boolean {
  const value = typeof type === "string" ? type.toUpperCase() : ""
  if (value === TransactionDirection.Income || value === TransactionDirection.TransferIn) return true
  return value.includes("INCOME") || (value.includes("TRANSFER") && value.includes("IN"))
}

export function isExpenseLikeTransactionType(type: unknown): boolean {
  const value = typeof type === "string" ? type.toUpperCase() : ""
  if (value === TransactionDirection.Expense || value === TransactionDirection.TransferOut) return true
  return value.includes("EXPENSE") || (value.includes("TRANSFER") && value.includes("OUT"))
}

const directionColorClass = {
  income: "text-emerald-600 dark:text-emerald-400",
  expense: "text-destructive",
  neutral: "text-muted-foreground",
} as const

type TransactionDirectionSignProps = {
  type: unknown
  className?: string
}

export function TransactionDirectionSign({ type, className }: TransactionDirectionSignProps) {
  const isIncome = isIncomeLikeTransactionType(type)
  const isExpense = isExpenseLikeTransactionType(type)

  if (!isIncome && !isExpense) {
    return <span className={cn("text-sm font-semibold tabular-nums", directionColorClass.neutral, className)}>—</span>
  }

  return (
    <span
      className={cn(
        "text-sm font-semibold tabular-nums",
        isIncome ? directionColorClass.income : directionColorClass.expense,
        className,
      )}
    >
      {isIncome ? "+" : "-"}
    </span>
  )
}

type TransactionAmountProps = {
  type: unknown
  amountLabel: string
  showSign?: boolean
  className?: string
}

export function TransactionAmount({ type, amountLabel, showSign = false, className }: TransactionAmountProps) {
  const isIncome = isIncomeLikeTransactionType(type)
  const isExpense = isExpenseLikeTransactionType(type)
  const colorClass =
    isIncome ? directionColorClass.income
    : isExpense ? directionColorClass.expense
    : "text-foreground"
  const prefix = showSign && isIncome ? "+" : showSign && isExpense ? "-" : ""

  return (
    <span className={cn("font-semibold tabular-nums", colorClass, className)}>
      {prefix}
      {amountLabel}
    </span>
  )
}
